/**
 * Menú "Mejorar resolución" — panel lateral en desktop, bottom sheet con
 * drag-to-dismiss en mobile — ADR-2026-09-16-los-menus-del-visor-son-componentes.
 *
 * Vivía embebido en el visor del historial (y su gemelo en el inline); salió acá
 * cuando el lienzo del Agente necesitó EL MISMO menú sin el visor. Es presentacional
 * a propósito, como `CarruselMenu`: la escala es controlada (el host la lee para el
 * guard de créditos antes de abrir), y el guard, la llamada y qué hacer con el
 * resultado los pone cada superficie.
 */
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Info, X } from 'lucide-react';
import { type Dimensiones, mayorEscalaQueEntra, upscaleEntraEnStorage } from '../lib/upscaleLimits';

export interface UpscaleMenuProps {
  open: boolean;
  /** URL (firmada) de la imagen a mejorar, para el preview. */
  imageUrl: string;
  /** La imagen ya tiene una versión mejorada: se avisa que se reemplaza y se cobra otra vez. */
  yaMejorada: boolean;
  scale: 2 | 4;
  onScaleChange: (scale: 2 | 4) => void;
  procesando: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

/** Tiempo de la transición de salida (coincide con `duration-300`). */
const DURACION_MS = 300;

export const UpscaleMenu: React.FC<UpscaleMenuProps> = ({
  open,
  imageUrl,
  yaMejorada,
  scale,
  onScaleChange,
  procesando,
  onClose,
  onConfirm,
}) => {
  // `montado` y `animado` van separados: al cerrar corre la transición de salida
  // ANTES de desmontar (mismo patrón que `CarruselMenu`).
  const [montado, setMontado] = useState(false);
  const [animado, setAnimado] = useState(false);
  const [dragY, setDragY] = useState(0);
  const dragStartY = useRef<number | null>(null);
  /**
   * Tamaño real del original, para no ofrecer una escala que Storage va a rechazar
   * (ADR-2026-09-22-mejorar-imagen-4x-no-entra-en-storage). Sale del preview que este
   * menú YA carga —`useUrlDelOriginal` firma el `full/`—, así que medirlo no cuesta una
   * descarga extra. Mientras no cargó (`null`) se ofrecen las dos: el guard del backend
   * es el que no se puede saltear, este sólo evita la espera al pedo.
   */
  const [dim, setDim] = useState<Dimensiones | null>(null);

  // Las medidas son de ESTA imagen y de ninguna otra. El menú no se desmonta al
  // cerrarse (sólo deja de renderizar: `if (!montado) return null`) y el host reusa la
  // misma instancia para cada selección del historial y del lienzo, así que sin este
  // reset las medidas de la imagen anterior siguen vivas: una foto chica heredaba el
  // "4x no entra" de una gigante y el effect de abajo le bajaba la escala sola, antes
  // de que su propio preview cargara.
  useEffect(() => {
    setDim(null);
  }, [imageUrl]);

  useEffect(() => {
    if (open) {
      setDragY(0);
      setMontado(true);
      return;
    }
    setAnimado(false);
    const id = setTimeout(() => {
      setMontado(false);
      setDragY(0);
    }, DURACION_MS);
    return () => clearTimeout(id);
  }, [open]);
  useEffect(() => {
    if (!montado) return;
    const id = requestAnimationFrame(() => setAnimado(true));
    return () => cancelAnimationFrame(id);
  }, [montado]);

  // Si el usuario ya tenía elegida una escala que no entra, se lo baja a la que sí:
  // el botón queda deshabilitado, pero la escala seleccionada es del host y hay que
  // corregirla ahí o "Mejorar" saldría con un valor imposible.
  const escalaMaxima = dim ? mayorEscalaQueEntra(dim) : null;
  useEffect(() => {
    if (escalaMaxima !== null && scale > escalaMaxima) onScaleChange(escalaMaxima);
  }, [escalaMaxima, scale, onScaleChange]);

  // Ninguna escala entra: el menú ya lo dice y deshabilita las dos opciones, pero
  // "Mejorar" seguía habilitado y mandaba igual la escala que estuviera elegida. El
  // backend lo rechaza —ahí está el guard que no se puede saltear—, así que no se
  // gastaba plata, pero era un viaje de ida y vuelta para recibir un error evitable.
  // `dim` null todavía no sabe nada: ahí se deja pasar, igual que las opciones.
  const ningunaEscalaEntra = dim !== null && escalaMaxima === null;

  if (!montado) return null;

  const costo = scale === 2 ? 1 : 2;

  const cuerpo = (activo: string) => (
    <>
      {imageUrl && (
        <div className="flex justify-center mb-6">
          <img
            src={imageUrl}
            alt="Preview"
            className="w-3/5 aspect-[4/5] object-cover rounded-[10px]"
            onLoad={(e) => {
              const img = e.currentTarget;
              if (img.naturalWidth > 0) setDim({ width: img.naturalWidth, height: img.naturalHeight });
            }}
          />
        </div>
      )}
      {yaMejorada && (
        <div className="mb-4 rounded-[13px] bg-warning/10 border border-warning/20 px-4 py-3 flex gap-3">
          <Info className="w-4 h-4 text-warning-text shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-[13px] font-semibold text-warning-text mb-0.5">Ya tenés una versión mejorada</p>
            <p className="text-[13px] text-warning-text/80 leading-snug">
              Al continuar vas a reemplazar la mejora actual y{' '}
              {costo === 1 ? 'se consumirá 1 imagen otra vez.' : `se consumirán ${costo} imágenes otra vez.`}
            </p>
          </div>
        </div>
      )}
      {/* Escala — iOS grouped section */}
      <div>
        <p className="text-[13px] font-normal text-[#3C3C43]/60 uppercase tracking-wide mb-2">Escala</p>
        <div className="rounded-[13px] bg-white overflow-hidden border border-neutral-200">
          <div className="px-4 py-[11px]">
            <div className="flex gap-2">
              {([2, 4] as const).map((e) => {
                const noEntra = dim ? !upscaleEntraEnStorage(dim, e) : false;
                return (
                  <button
                    key={e}
                    type="button"
                    disabled={noEntra}
                    title={noEntra ? 'Esta imagen es demasiado grande para esta escala' : undefined}
                    onClick={() => onScaleChange(e)}
                    className={`flex-1 h-10 rounded-lg text-[15px] font-medium transition-colors ${
                      noEntra
                        ? 'bg-neutral-100 text-brand-black/25 cursor-not-allowed'
                        : scale === e
                          ? 'bg-brand-primary text-white'
                          : `bg-neutral-100 text-brand-black/70 ${activo}`
                    }`}
                  >
                    {e}x
                  </button>
                );
              })}
            </div>
            {escalaMaxima !== null && escalaMaxima < 4 && (
              <p className="text-[13px] text-[#3C3C43]/60 mt-2 leading-snug">
                Esta imagen ya es muy grande: la podemos mejorar hasta {escalaMaxima}x.
              </p>
            )}
            {dim && escalaMaxima === null && (
              <p className="text-[13px] text-warning-text mt-2 leading-snug">
                Esta imagen es demasiado grande para mejorarla más.
              </p>
            )}
          </div>
        </div>
      </div>
      {/* Información — iOS grouped section */}
      <div className="mt-6">
        <p className="text-[13px] font-normal text-[#3C3C43]/60 uppercase tracking-wide mb-2">Información</p>
        <div className="rounded-[13px] bg-white overflow-hidden border border-neutral-200">
          <div className="px-4 py-[11px]">
            <div className="flex items-center gap-3 mb-2">
              <span className="material-symbols-rounded text-brand-primary text-[20px] shrink-0">
                photo_size_select_large
              </span>
              <span className="text-[15px] text-brand-black/90">Resolución {scale}x</span>
            </div>
            <p className="text-[13px] text-[#3C3C43]/60">
              La imagen se mejorará a {scale} veces su resolución original usando inteligencia artificial.
            </p>
          </div>
          <div className="ml-[46px] h-px bg-black/[0.08]" />
          <div className="px-4 py-[11px]">
            <div className="flex items-center gap-2">
              <Info className="w-3.5 h-3.5 text-brand-primary shrink-0" />
              <span className="text-[13px] text-[#3C3C43]/60">
                {costo === 1 ? 'Se consumirá 1 imagen' : `Se consumirán ${costo} imágenes`}
              </span>
            </div>
          </div>
        </div>
      </div>
    </>
  );

  // Portaleado a document.body: el lightbox también va por portal en la raíz; inline
  // dentro del shell `transform-gpu` el z-index quedaría atrapado debajo.
  return createPortal(
    <>
      <div
        className={`fixed inset-0 z-[200] bg-black/40 transition-opacity duration-300 ${animado ? 'opacity-100' : 'opacity-0'}`}
        onClick={onClose}
      />
      {/* Desktop: side panel */}
      <div
        className={`hidden sm:flex fixed top-4 right-4 bottom-4 w-[420px] bg-white rounded-[13px] shadow-[0_8px_40px_rgba(0,0,0,0.12),0_0_0_0.5px_rgba(0,0,0,0.06)] z-[201] flex-col overflow-hidden transition-transform duration-300 ease-out ${animado ? 'translate-x-0' : 'translate-x-[calc(100%+32px)]'}`}
      >
        <div className="flex items-center justify-between px-5 py-4">
          <h3 className="text-[17px] font-semibold text-brand-black/90">Mejorar resolución</h3>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 inline-flex items-center justify-center rounded-full bg-black/[0.06] text-brand-black/40 hover:bg-black/[0.1] transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="mx-5 h-px bg-black/[0.08]" />
        <div className="flex-1 overflow-y-auto px-5 py-4">{cuerpo('hover:bg-neutral-200')}</div>
        <div className="px-5 py-3 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="text-[15px] text-[#3C3C43]/60 hover:text-brand-black/80 px-3 py-2 transition-colors"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={procesando || ningunaEscalaEntra}
            className="text-[15px] font-semibold text-white bg-brand-primary hover:bg-brand-primary-hover rounded-md px-4 py-2 shadow-sm transition-colors disabled:opacity-50"
          >
            {procesando ? 'Procesando...' : 'Mejorar'}
          </button>
        </div>
      </div>
      {/* Mobile: full-height slide-up sheet */}
      <div
        className="sm:hidden fixed inset-x-0 bottom-0 z-[201] bg-[#F2F2F7] rounded-t-[13px] flex flex-col"
        style={{
          height: 'calc(100dvh - 1rem - env(safe-area-inset-top, 0px))',
          paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)',
          transform: dragY > 0 ? `translateY(${dragY}px)` : animado ? 'translateY(0)' : 'translateY(100%)',
          transition: dragY > 0 ? 'none' : 'transform 300ms ease-out',
        }}
      >
        <div
          className="flex justify-center pt-2 pb-1 cursor-grab active:cursor-grabbing shrink-0"
          onTouchStart={(e) => {
            dragStartY.current = e.touches[0].clientY;
          }}
          onTouchMove={(e) => {
            if (dragStartY.current !== null) {
              const dy = e.touches[0].clientY - dragStartY.current;
              if (dy > 0) setDragY(dy);
            }
          }}
          onTouchEnd={() => {
            dragStartY.current = null;
            if (dragY > 100) onClose();
            else setDragY(0);
          }}
        >
          <div className="w-9 h-[5px] rounded-full bg-neutral-300" />
        </div>
        <div className="flex items-center justify-between px-5 py-3 shrink-0">
          <h3 className="text-[15px] font-semibold text-brand-black/90">Mejorar resolución</h3>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 inline-flex items-center justify-center rounded-full bg-black/[0.06] text-brand-black/40 active:bg-black/[0.1] transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="mx-5 h-px bg-black/[0.08] shrink-0" />
        <div className="flex-1 overflow-y-auto px-5 py-4">{cuerpo('active:bg-neutral-200')}</div>
        {/* Footer — solo "Mejorar" full-width; la X de arriba cierra. */}
        <div className="px-5 py-3 shrink-0">
          <button
            type="button"
            onClick={onConfirm}
            disabled={procesando || ningunaEscalaEntra}
            className="w-full text-[15px] font-semibold text-white bg-brand-primary active:bg-brand-primary-hover rounded-md px-4 py-3 shadow-sm transition-colors disabled:opacity-50"
          >
            {procesando ? 'Procesando...' : 'Mejorar'}
          </button>
        </div>
      </div>
    </>,
    document.body,
  );
};

export default UpscaleMenu;
